import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

export const EventVideoSection = () => {
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();

  return (
    <section
      id="event-video"
      className="py-12 lg:py-16 relative overflow-hidden"
      data-testid="event-video-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/offline-event-bg.png')" }}
      />
      {/* Dark Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-gold-400 font-medium text-sm tracking-wider uppercase mb-2 block">
            Offline Event
          </span>
          <h2 className="font-sans text-3xl md:text-4xl lg:text-5xl font-[720] text-white mb-2">
            오프라인 이벤트
          </h2>
          
          {/* Main Quote */}
          <p className="font-sans text-xl md:text-2xl lg:text-3xl font-[720] text-gold-300 mb-10 leading-[1.6]">
            "좋은 인연을 만나는 가장 우아한 방법"
          </p>
          
          {/* Description */}
          <div className="max-w-4xl mx-auto text-white/90 text-base md:text-lg leading-[2] md:leading-[2.1] space-y-7 md:space-y-8">
            <p>
              누구를 만날지 모르는 불안함은 내려놓으세요.<br className="hidden md:block" />
              철저한 신원 확인과 매너 있는 에티켓 가이드를 통해, 격식 있고 편안한 만남의 품격을 유지합니다.
            </p>
            <p className="text-gold-300 font-medium">
              당신의 소중한 시간을 가장 가치 있게 만드는 방법,<br className="hidden md:block" />
              리즈가 제안하는 프리미엄 오프라인 이벤트입니다.
            </p>
          </div>
        </div>

        {/* Video Thumbnail Card - 30% smaller */}
        <div className="max-w-2xl mx-auto">
          <div 
            className="relative rounded-2xl overflow-hidden shadow-2xl cursor-pointer group"
            onClick={() => setShowVideo(true)}
          >
            {/* Thumbnail Image - using video poster or gradient */}
            <div className="aspect-video bg-gradient-to-br from-stone-800 via-stone-700 to-stone-900 relative">
              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-xl transition-all group-hover:scale-110">
                  <Play className="w-6 h-6 md:w-8 md:h-8 text-burgundy-600 ml-1" fill="currentColor" />
                </div>
              </div>
              
              {/* Text Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white font-medium text-base md:text-lg">
                  Valentine 2026 이벤트 스케치
                </p>
                <p className="text-white/70 text-xs md:text-sm">
                  클릭하여 영상 보기
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-6">
            <Button 
              size="lg"
              className="bg-gold-400 hover:bg-gold-500 text-stone-800 font-semibold"
              onClick={() => navigate('/offline-events')}
              data-testid="event-video-detail-btn"
            >
              자세히 보기
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setShowVideo(false)}
        >
          <div 
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              className="absolute -top-12 right-0 text-white hover:text-gold-400 transition-colors"
              onClick={() => setShowVideo(false)}
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video */}
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <video
                className="w-full aspect-video"
                controls
                autoPlay
                data-testid="event-video"
              >
                <source
                  src="https://customer-assets.emergentagent.com/job_c31d3ebc-0781-4769-9553-55de188c1b77/artifacts/y3pmkx9h_%5Bvalentine%202026%5D%EC%8A%A4%EC%BC%80%EC%B9%98%EC%98%81%EC%83%81%28%EB%B0%B0%EA%B2%BD%EC%9D%8C%EC%95%85%20%EA%B5%90%EC%B2%B4%29.mp4"
                  type="video/mp4"
                />
                브라우저가 비디오를 지원하지 않습니다.
              </video>
            </div>
            
            {/* Caption */}
            <p className="text-center text-white/70 text-sm mt-4">
              Valentine 2026 이벤트 스케치 영상
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
